// Wire protocol. Keys are short to keep 20 Hz snapshots small.
import type { BossKind, ChallengeKind, HackId, NpcKind, VehicleType } from './data';
import type { Character, ThrowKind } from './rules';
import type { InputCmd } from './sim';

/** Crew masks (art in client/src/crew.ts). Cosmetic; picked on the join screen and sent in the socket handshake. */
export const MASK_COUNT = 4;
export const maskOf = (v: unknown): number => (Number.isInteger(v) && (v as number) >= 0 && (v as number) < MASK_COUNT ? (v as number) : -1);

export interface PlayerSnap {
  id: number; n: string; x: number; y: number; a: number; hp: number; mhp: number; ar: number;
  ram: number; mram: number; v: number; s: number; d: 0 | 1; dt: number;
  h: number; ph: 0 | 1 | 2; st: number; w: string; wr: string; rel: number; mag: number; res: number;
  ch: number; // charge 0..1
  ap: [number, number, number]; // appearance indices skin,jacket,hair
  mk: number; // crew mask picked on the join screen (0..MASK_COUNT-1), -1 = none: fall back to the crew slot
  fx: number; // bit 1 reflex active, 2 vaulting, 4 disconnected, 8 test client, 16 healing on the clinic pad, 32 pad paused, 64 Ryujin Overclock running
  ab: number; abm: number; // ability cooldown left / max
  hk: Record<string, number>; // hack cooldowns left
  cc: number; // crime chain count
  rs: number; // revive progress 0..1
  spd: number; // current walk speed (px/s) for prediction
  hot: number; // 1 if police currently consider this player a target
  lx?: number; ly?: number; // where police last saw this player (only while they search)
  sl: number; // active weapon slot (0 fists, 1-3 equipped)
  bl: number; // recoil bloom + drift (rad) of the gun in hand, for the crosshair
  gp?: number; // parts fitted to the gun in hand, for display (gunsmith.ts heldPartBits: 1 optic, 2 suppressor, 4 extended mag); left out when 0
  bu?: number; // Kaminari-gumi burn: whole seconds left (left out when not burned)
}
export interface VehicleSnap {
  id: number; t: VehicleType; x: number; y: number; a: number; vx: number; vy: number;
  hp: number; mhp: number; o: number[]; // occupants per seat: player id, -1 npc, 0 empty
  pc: number; // paint colour index
  f: number;  // 1 siren, 2 remote-controlled, 4 challenge target, 8 personal(garage), 16 wrecked, 32 stalled (EMP)
  rad: number; re: number; // radio station, epoch ms
  own: number; // player id of personal vehicle owner or 0
}
export interface NpcSnap { id: number; k: NpcKind; x: number; y: number; a: number; hp: number; mhp: number; st: number; j: number; g?: string /* faction gun id (weapons.json / gunsmith), left out when none */ }
/** A crew-job boss. w = warning seconds before it arrives (0 once it's in the fight); ph = phase 0..;
 *  atk = current attack ('' when none), at = its windup (or breath) seconds left, on = 1 while it is striking (breath,
 *  charge, gun burst) rather than winding up; aa/arc/rg = aim, cone width and reach; z = pending ground blasts [x, y, radius, seconds left]. */
export interface BossSnap {
  id: number; k: BossKind; x: number; y: number; a: number; hp: number; mhp: number; ph: number; w: number;
  atk: string; at: number; on: number; aa: number; arc: number; rg: number; z: number[][];
}
/** A Kagemusha hologram: id = owner player id (one per player), l = seconds left, m = its full life. */
export interface DecoySnap { id: number; x: number; y: number; a: number; l: number; m: number }
export interface ProjSnap { id: number; x: number; y: number; vx: number; vy: number; k: number }
export interface InfraSnap {
  sig: number[];           // per intersection: 0 NS green,1 EW green,2 all-red override
  bar: number[];           // per barrier: 1 raised
  cam: [number, number][]; // [angle*100, jammed]
  fix: number[];           // hazard seconds left
  vent: number[];          // per steam vent: seconds left open (0 = closed)
  res: Record<string, number>; // reserved target key -> seconds left
}
export interface ChallengeSnap {
  id: string; k: ChallengeKind; hard: boolean; stage: string; tl: number; owner: number; parts: number[];
  tx: number; ty: number; // current objective point
  tv: number; // target vehicle id
  carrier: number; stars: number;
  bk?: BossKind; // boss hunts only: which boss
  h?: string; // cred-unlocked jobs: the current objective line
}
export interface PickupSnap { id: string; x: number; y: number; k: 'cash' | 'weapon' | 'ammo' | 'package' | 'star'; label: string; col: string }
export interface PingSnap { id: number; x: number; y: number; k: 'enemy' | 'object' | 'destination'; by: number; t: number; lk?: 1 /* auto-mark from the Lookout perk */ }
/** q = suppressed shot. 'dmg' is silent feedback: d = health lost, n = npc hit, to = player hit, by = attacking player */
export interface GameEvent { k: 'shot' | 'hit' | 'boom' | 'hack' | 'crash' | 'melee' | 'pickup' | 'vault' | 'spark' | 'dmg' | 'throw' | 'smoke' | 'emp' | 'decoy'; x: number; y: number; w?: string; a?: number /* swing/throw direction */; by?: number; d?: number; n?: number; to?: number; q?: 1;
  /** accepted hacks only: the stable target key ("vent:1") and the server's seconds until the state ends (0 = instant effect) */
  tk?: string; du?: number }

/** A smoke cloud: centre, radius, seconds left (client animates the puffs) */
export interface SmokeSnap { id: number; x: number; y: number; r: number; t: number }

export interface Snapshot {
  t: number; tick: number; ack: number; you: number; leader: number;
  p: PlayerSnap[]; v: VehicleSnap[]; n: NpcSnap[]; pr: ProjSnap[]; inf: InfraSnap;
  sty?: { x: number; y: number }; // story mode only: where your marked story car is (left out otherwise)
  cm?: number; // seconds of Data Broker camera map left for you (left out when none)
  c: ChallengeSnap[]; pk: PickupSnap[]; pg: PingSnap[]; ev: GameEvent[];
  ff: boolean;
  dc?: DecoySnap[]; // Kagemusha decoys; left out when there are none
  b?: BossSnap[]; // crew-job bosses; left out when there are none
  cb?: { id: number; n: string }; // the Kumadori Club boss (an NPC id) and his name, while he is in the club
  /** Shingan (M10) while he is in the monastery: his NPC id, name, his hologram decoys' NPC ids, wu 1 while his naginata
   *  winds up, st seconds left of the strip (no decoys, no blink), bl where a blink started (shown briefly) */
  sg?: { id: number; n: string; dc: number[]; /** escorts (presentation: the security-monk look) */ es: number[]; wu: 0 | 1; st: number; bl?: [number, number] };
  sm?: SmokeSnap[]; // smoke clouds; left out when there are none
}
export interface Welcome {
  you: number; code: string; leader: number; character: Character; serverTime: number;
  testHooks: boolean; ff: boolean;
}
export interface PagerMsg { text: string; col?: string; from?: string; kind: 'chat' | 'sys' | 'pager' | 'reward' | 'warn'; rc?: import('./hacks').RefusalCode /* a refused hack: why (presentation only) */ }

export type ActRequest =
  | { t: 'interact'; exit?: boolean } | { t: 'reload' } | { t: 'throw'; kind?: ThrowKind } | { t: 'ability' } | { t: 'weapon'; slot: number }
  | { t: 'respawn' } | { t: 'radio' } | { t: 'revive' }
  | { t: 'hack'; hack: HackId; target: string; ax?: number; ay?: number }
  | { t: 'ping'; x: number; y: number }
  | { t: 'chat'; text: string }
  | { t: 'setting'; ff: boolean }
  | { t: 'story'; op: 'start' | 'reset' }
  | { t: 'job'; op: 'start' | 'join' | 'leave'; kind?: ChallengeKind; hard?: boolean; id?: string; boss?: BossKind }
  | { t: 'buy'; req: string; item: 'weapon' | 'ammo' | 'stars' | 'smoke' | 'emp' | 'cyber' | 'vehicle' | 'upgrade' | 'intel'; id: string; rarity?: string; vtype?: VehicleType }
  | { t: 'sell_vehicle'; req: string }
  | { t: 'sell'; req: string; uid?: string; part?: string } // a gun (by item uid) or one spare part, at Hachi's
  | { t: 'respray'; req: string }
  | { t: 'bribe'; req: string }
  | { t: 'garage'; op: 'spawn' | 'store'; vtype?: VehicleType }
  | { t: 'equip'; slot: number; uid: string | null }
  /** gunsmith at Hachi's: put `part` in slot `path` of modular gun `uid` (null removes), or apply preset n (0 = stock) */
  | { t: 'mod'; req: string; uid: string; path?: string; part?: string | null; preset?: number }
  | { t: 'cyber'; op: 'install' | 'uninstall' | 'setActive'; id: string }
  | { t: 'attr'; id: string } | { t: 'perk'; id: string } | { t: 'undo' } | { t: 'respec' }
  | { t: 'appearance'; ap: [number, number, number, number]; name?: string };

export interface ClientToServer {
  input: (cmds: InputCmd[]) => void;
  act: (a: ActRequest) => void;
}
export interface ServerToClient {
  welcome: (w: Welcome) => void;
  snap: (s: Snapshot) => void;
  char: (c: Character) => void;
  msg: (m: PagerMsg) => void;
  kicked: (reason: string) => void;
  ui: (u: { open: 'shop' | 'jobs'; poi: string }) => void;
}

/** Target key format for hacks: "sig:3", "bar:1", "cam:4", "fix:2", "vent:1", "veh:17", "npc:40" */
export type TargetKey = string;
